import { useLifecyclePersistence, useSyncLifecycleToExistingAtom } from '@/ui'
import { ConformedCampaignFeedAd, MediaAsset } from '@/ui'
import { useImageCacheWriter } from '@/ui'
import {
	feedAdAtom,
	feedAdHasGeneratedContentAtom,
	feedAdImageHistoryAtom,
	feedAdSavingAtom,
	feedAdStatusAtom,
	useFeedAdListener,
} from '@/ui'
import { trackDesignSaved } from '@/ui'
import { cacheEditorImages, type EditorImage } from '@/ui/editor/image-readiness'
import { useSetAtom } from 'jotai'
import { useEffect } from 'react'

export type UseFeedEditorArguments = {
	feedAd?: ConformedCampaignFeedAd

	// should reflect if the feed ad's query is pending
	isPending: boolean

	// should reflect if the feed ad's data indicate it's still being generated
	isGenerating: boolean

	// should reflect if a chat tool is currently executing on the feed ad
	isToolExecuting?: boolean

	//
	imagesHistory?: Array<MediaAsset>

	// pass in the existing image, if present. This doesn't affect the actual
	// bound image; it's just for caching the image URL.
	image?: EditorImage

	// callback to handle feed ad changes; will be debounced internally
	onFeedAdChange: (value: ConformedCampaignFeedAd) => void

	// optional version tracking for preventing race conditions
	feedAdVersion?: number | string | Date | null
}

export function useFeedEditor(
	args: UseFeedEditorArguments,
): { lifecycle: 'idle' | 'pending' | 'generating' | 'tool-executing' | 'ready' } {
	const {
		feedAd,
		isPending,
		isGenerating,
		isToolExecuting = false,
		imagesHistory,
		image,
		onFeedAdChange,
		feedAdVersion,
	} = args

	const { cacheImageUrl } = useImageCacheWriter()

	// Set up lifecycle persistence
	const feedLifecycle = useLifecyclePersistence<ConformedCampaignFeedAd>({
		data: feedAd ?? undefined,
		isIdle: (d) => d == undefined,
		isPending: () => isPending,
		isGenerating: () => isGenerating,
		isToolExecuting,
		save: async (feedData) => {
			await onFeedAdChange(feedData)
			if (feedData?.id) {
				trackDesignSaved('feed', feedData.id)
			}
		},
		debounceMs: 800,
		getVersion: () => feedAdVersion ?? null,
		debug: false,
		name: 'useFeedEditor',
	})

	// Sync to existing atoms
	// Note: feedAdAtom can be undefined when data hasn't loaded yet
	useSyncLifecycleToExistingAtom<ConformedCampaignFeedAd, ConformedCampaignFeedAd | undefined>(
		feedLifecycle,
		{
			dataAtom: feedAdAtom,
			useDataListener: useFeedAdListener,
			lifecycleAtom: feedAdStatusAtom,
			persistenceAtom: feedAdSavingAtom,
		},
	)

	// Image caching (unchanged from original implementation)
	const setFeedAdImageHistory = useSetAtom(feedAdImageHistoryAtom)
	const setHasGeneratedContent = useSetAtom(feedAdHasGeneratedContentAtom)
	useEffect(() => {
		cacheEditorImages(image, imagesHistory, cacheImageUrl)
		if (imagesHistory) {
			setFeedAdImageHistory(imagesHistory)
		}
	}, [imagesHistory, setFeedAdImageHistory, cacheImageUrl, image])

	useEffect(() => {
		if (!feedAd) {
			setHasGeneratedContent(false)
			return
		}

		if (!isGenerating) {
			setHasGeneratedContent(true)
		}
	}, [feedAd, isGenerating, setHasGeneratedContent])

	return { lifecycle: feedLifecycle.lifecycle }
}
